import { useMemo } from 'react';
import { IconAlertTriangle as AlertTriangle, IconMap as MapIcon } from '@tabler/icons-react';
import { buildLayout, seatCategoryColor } from '@/components/event-seating/seating';

interface Props {
    venueName?: string;
    seatMap?: any;
    hasVenue: boolean;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
    canEditVenue: boolean;
    onEditVenue: () => void;
    onSelectVenue: () => void;
}

export default function VenueMapPreview({ venueName, seatMap, hasVenue, isLoading, isError, onRetry, canEditVenue, onEditVenue, onSelectVenue }: Props) {
    const layout = useMemo(() => buildLayout(seatMap), [seatMap]);
    const tiered = layout.categories.length > 0;
    const untiered = tiered ? layout.seats.filter(seat => !seat.category).length : 0;
    const empty = !isLoading && !isError && (!hasVenue || layout.seats.length === 0);

    return (
        <section aria-label="Venue map preview" className="mb-8 rounded-[22px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] p-4 sm:p-5">
            <div className="mb-3 flex items-center gap-2">
                <MapIcon size={16} aria-hidden />
                <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-grey10 dark:text-white" title={venueName}>{venueName || 'Venue map'}</h3>
                {canEditVenue && hasVenue && !isError && (
                    <button type="button" onClick={onEditVenue} className="h-9 px-4 rounded-full ring-1 ring-black/10 dark:ring-white/15 text-sm whitespace-nowrap text-grey10 dark:text-white hover:bg-white dark:hover:bg-white/10">
                        Edit Venue Map
                    </button>
                )}
            </div>

            {isLoading && <div aria-busy="true" className="h-48 rounded-2xl bg-white dark:bg-white/[0.06] animate-pulse" />}

            {isError && (
                <div role="alert" className="flex items-center justify-between gap-3 rounded-2xl border border-danger p-4 text-sm">
                    <span>The venue map could not be loaded.</span>
                    <button type="button" className="underline whitespace-nowrap" onClick={onRetry}>Retry</button>
                </div>
            )}

            {empty && (
                <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-grey90 dark:border-nightBorder py-10 text-center">
                    <p className="text-sm text-grey40 dark:text-nightMuted">No venue map added yet. Select a venue to preview its layout.</p>
                    <button type="button" onClick={onSelectVenue} className="h-9 px-4 rounded-full ring-1 ring-black/10 dark:ring-white/15 text-sm whitespace-nowrap text-grey10 dark:text-white hover:bg-white dark:hover:bg-white/10">
                        Select venue
                    </button>
                </div>
            )}

            {!isLoading && !isError && layout.seats.length > 0 && (
                <>
                    <svg
                        role="img"
                        aria-label={`Preview of ${venueName || 'the venue'} map`}
                        viewBox={`${layout.bounds.x} ${layout.bounds.y} ${layout.bounds.width} ${layout.bounds.height}`}
                        preserveAspectRatio="xMidYMid meet"
                        className="h-56 w-full rounded-2xl bg-white dark:bg-black/30"
                    >
                        {layout.shapes.map(shape => (
                            <rect key={shape.key} x={shape.position.x} y={shape.position.y} width={shape.size.width} height={shape.size.height} fill="#9ca3af" opacity={0.5} />
                        ))}
                        {layout.seats.map(seat => (
                            <circle key={seat.id} cx={seat.x} cy={seat.y} r={8} fill={seatCategoryColor(layout, seat)} />
                        ))}
                    </svg>
                    {tiered && (
                        <ul className="mt-3 flex flex-wrap gap-2" aria-label="Tiers">
                            {layout.categories.map(category => (
                                <li key={category.name} className="inline-flex items-center gap-2 rounded-full border border-grey90 dark:border-nightBorder px-3 py-1 text-xs">
                                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: category.color }} aria-hidden />
                                    <span className="max-w-[160px] truncate" title={category.name}>{category.name}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {untiered > 0 && (
                        <p className="mt-3 flex items-start gap-2 text-sm text-warning30 dark:text-warning60">
                            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" aria-hidden />
                            <span>{untiered} seats have no tier and can't be sold. Assign them in the venue map.</span>
                        </p>
                    )}
                </>
            )}
        </section>
    );
}
